import {
  NomObjectClass,
  NomObjectLifecycleStatus,
} from 'src/app/core/constants';
import { UserAccessLevel } from 'src/app/core/services/user.service';
import { OBJECT_SETTINGS_ACCESS_LEVEL } from 'src/app/core/object-settings-access-level.constants';

/**
 * Availability metadata of the object-card dropdown actions - the single
 * place that answers "would the menu have any items?" so the cell renderer
 * shows the hamburger only when opening it yields a non-empty menu.
 *
 * KEEP IN SYNC with ObjectCardDropdownComponent's action arrays: one entry
 * per non-separator action, carrying only the gates (`accessLevel`,
 * `requiresEdit`) - labels, icons and handlers stay on the component.
 * COMPANY is deliberately absent: its dropdown offers only Settings, which
 * the object-title link already covers, so company cells get no hamburger.
 */
interface ObjectCardActionAvailability {
  accessLevel?: UserAccessLevel;
  requiresEdit?: boolean;
}

const LIFECYCLE_KEYED_ACTIONS: Partial<
  Record<NomObjectLifecycleStatus, ObjectCardActionAvailability[]>
> = {
  // settings / copy / archive / delete - copy is ungated, so an active
  // object always has a menu
  [NomObjectLifecycleStatus.ACTIVE]: [
    { requiresEdit: true },
    {},
    { requiresEdit: true },
    { requiresEdit: true },
  ],
  // unarchive / delete
  [NomObjectLifecycleStatus.ARCHIVED]: [
    { requiresEdit: true },
    { requiresEdit: true },
  ],
  // restore
  [NomObjectLifecycleStatus.DELETED]: [{ requiresEdit: true }],
};

// toggle execution status / delete; the dropdown shows them regardless of
// lifecycle, but the hamburger convention keeps them to active rows
const ALERT_SCHEDULE_ACTIONS: ObjectCardActionAvailability[] = [
  { requiresEdit: true },
  { requiresEdit: true },
];

// settings / change password / activity + search + export logs / app login
const USER_ACTIONS: ObjectCardActionAvailability[] = [
  { accessLevel: OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.USER] },
  { accessLevel: UserAccessLevel.CLIENT_ADMIN },
  { accessLevel: UserAccessLevel.CLIENT_ADMIN },
  { accessLevel: UserAccessLevel.CLIENT_ADMIN },
  { accessLevel: UserAccessLevel.CLIENT_ADMIN },
  { accessLevel: UserAccessLevel.GLOBAL_ADMIN },
];

// settings / delete
const GROUP_ROLE_ACTIONS: ObjectCardActionAvailability[] = [
  { accessLevel: OBJECT_SETTINGS_ACCESS_LEVEL[NomObjectClass.ROLE] },
  { accessLevel: UserAccessLevel.SYSTEM_ADMIN },
];

// archive / delete
const RULE_ACTIONS: ObjectCardActionAvailability[] = [{}, {}];

/**
 * Would the object-card dropdown contain at least one action for this
 * object and viewer? `stripRequiresEdit` mirrors the dropdown's OBJECT_
 * PERMISSIONS rule: when the cell's object IS the row and the row carries
 * `can_edit: false`, the write actions are filtered out.
 */
export function hasObjectCardMenuItems(
  objectClassId: NomObjectClass,
  lifecycleStatus: NomObjectLifecycleStatus | null | undefined,
  userAccessLevel: UserAccessLevel,
  stripRequiresEdit: boolean = false,
): boolean {
  let actions: ObjectCardActionAvailability[];

  switch (objectClassId) {
    case NomObjectClass.FEED:
    case NomObjectClass.DASHBOARD:
    case NomObjectClass.WORKSPACE:
      actions = LIFECYCLE_KEYED_ACTIONS[lifecycleStatus] ?? [];
      break;
    case NomObjectClass.ALERT:
    case NomObjectClass.EXPORT_SCHEDULE:
      actions =
        lifecycleStatus === NomObjectLifecycleStatus.ACTIVE
          ? ALERT_SCHEDULE_ACTIONS
          : [];
      break;
    case NomObjectClass.USER:
      actions = USER_ACTIONS;
      break;
    case NomObjectClass.USER_GROUP:
    case NomObjectClass.ROLE:
      actions = GROUP_ROLE_ACTIONS;
      break;
    case NomObjectClass.RULE:
      actions = RULE_ACTIONS;
      break;
    default:
      return false;
  }

  return actions.some(
    (action) =>
      (action.accessLevel == null || userAccessLevel >= action.accessLevel) &&
      !(stripRequiresEdit && action.requiresEdit),
  );
}
